import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export const AdminTour = ({ open, onClose, steps = [], storageKey = 'adminTourCompleted', initialIndex = 0, onIndexChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [index, setIndex] = useState(initialIndex);
  const [targetRect, setTargetRect] = useState(null);
  const [waitingForNav, setWaitingForNav] = useState(false);

  const current = steps[index];

  const resolved = useMemo(() => {
    if (!current?.selector) return { el: null, rect: null };
    const el = document.querySelector(current.selector);
    if (!el) return { el: null, rect: null };
    const rect = el.getBoundingClientRect();
    return { el, rect };
  }, [current?.selector, index, open]);

  useEffect(() => {
    if (!open) return;
    const max = Math.max(0, steps.length - 1);
    const nextIndex = Math.min(Math.max(0, Number.isFinite(initialIndex) ? initialIndex : 0), max);
    setIndex(nextIndex);
    setWaitingForNav(false);
  }, [open, initialIndex, steps.length]);

  useEffect(() => {
    onIndexChange?.(index);
  }, [index, onIndexChange]);

  // Handle navigation to step's path
  useEffect(() => {
    if (!open || !current) return;
    if (current.path && location.pathname !== current.path) {
      setWaitingForNav(true);
      navigate(current.path);
    } else {
      setWaitingForNav(false);
    }
  }, [open, index, current?.path, location.pathname, navigate]);

  // Wait for navigation to complete before showing highlight
  useEffect(() => {
    if (!open || !waitingForNav) return;
    if (current?.path && location.pathname === current.path) {
      const timer = setTimeout(() => setWaitingForNav(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open, waitingForNav, current?.path, location.pathname]);

  useEffect(() => {
    if (!open) return;

    const update = () => {
      if (!current?.selector) {
        setTargetRect(null);
        return;
      }
      const el = document.querySelector(current.selector);
      if (!el) {
        setTargetRect(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, current?.selector]);

  useEffect(() => {
    if (!open) return;
    if (resolved.el) {
      try {
        resolved.el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      } catch {
        // ignore
      }
    }
  }, [open, index, resolved.el]);

  if (!open || !current) return null;
  if (waitingForNav) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
        <div className="text-white text-lg">Carregando...</div>
      </div>
    );
  }

  const finish = () => {
    localStorage.setItem(storageKey, 'true');
    onClose?.();
  };

  const skip = () => {
    localStorage.setItem(storageKey, 'true');
    onClose?.();
  };

  const next = () => {
    if (index >= steps.length - 1) {
      finish();
      return;
    }
    setIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const prev = () => setIndex((i) => Math.max(i - 1, 0));

  const padding = 10;
  const highlight = targetRect
    ? {
        top: Math.max(0, targetRect.top - padding),
        left: Math.max(0, targetRect.left - padding),
        width: Math.min(window.innerWidth, targetRect.width + padding * 2),
        height: Math.min(window.innerHeight, targetRect.height + padding * 2),
      }
    : null;

  const tooltipWidth = 360;
  const tooltipHeightEstimate = 220;
  const margin = 16;

  const left = highlight
    ? Math.min(window.innerWidth - tooltipWidth - margin, Math.max(margin, highlight.left))
    : margin;

  let top = 80;
  if (highlight) {
    const below = highlight.top + highlight.height + 12;
    const above = highlight.top - tooltipHeightEstimate - 12;
    const fitsBelow = below + tooltipHeightEstimate + margin <= window.innerHeight;
    const fitsAbove = above >= margin;

    if (fitsBelow) {
      top = below;
    } else if (fitsAbove) {
      top = above;
    } else {
      top = Math.min(
        window.innerHeight - tooltipHeightEstimate - margin,
        Math.max(margin, highlight.top + 12)
      );
    }
  }

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black/70" onClick={skip} />

      {highlight && (
        <div
          className="absolute rounded-xl border-2 border-cyan-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.70)] pointer-events-none"
          style={{
            top: `${highlight.top}px`,
            left: `${highlight.left}px`,
            width: `${highlight.width}px`,
            height: `${highlight.height}px`,
          }}
        />
      )}

      <div
        className="absolute rounded-2xl border border-slate-700 bg-slate-900/95 text-white shadow-2xl p-5"
        style={{ width: tooltipWidth, top, left }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs text-slate-400">Passo {index + 1} de {steps.length}</div>
            <div className="text-lg font-semibold mt-1">{current.title}</div>
          </div>
          <button
            type="button"
            className="text-slate-300 hover:text-white"
            onClick={skip}
          >
            Pular
          </button>
        </div>

        {current.description && (
          <div className="text-sm text-slate-300 mt-3 leading-relaxed">{current.description}</div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            className={`px-4 py-2 rounded-xl border border-slate-700 ${index === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-800'}`}
            onClick={prev}
            disabled={index === 0}
          >
            Voltar
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded-xl border border-slate-700 hover:bg-slate-800"
              onClick={finish}
            >
              Encerrar
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-700"
              onClick={next}
            >
              {index >= steps.length - 1 ? 'Concluir' : 'Próximo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
